import { useSwipe, usePointerSwipe } from '@vueuse/core'
import { inBrowser, isFunction, isBlank } from 'vessel/utils'

const touchSupported = inBrowser
  ? 'ontouchstart' in window
  : true

const defaults = {
  disableClick: false,
  tapTolerance: 10,
  swipeTolerance: 50,
  longTapTimeInterval: 400
}

const updateOptions = (el, { value }) => {
  el.useTouchOptions = { ...defaults, ...el.useTouchOptions, ...value }
}

const addCallbacks = (el, { arg, modifiers, value }) => {
  if (!el.useTouchCallbacks) {
    el.useTouchCallbacks = {}
  }

  if (!el.useTouchCallbacks[arg]) {
    el.useTouchCallbacks[arg] = []
  }

  if (isFunction(value)) {
    const types = isBlank(modifiers) ? [null] : Object.keys(modifiers)

    types.forEach(type => {
      el.useTouchCallbacks[arg].push({ type, callback: value })
    })
  }
}

const removeCallbacks = (el, { arg }) => {
  if (el.useTouchCallbacks) {
    delete el.useTouchCallbacks[arg]
  }
}

const eachCallback = (el, arg, handler) => {
  if (el.useTouchCallbacks) {
    const callbacks = el.useTouchCallbacks[arg] || []
    callbacks.forEach(handler)
  }
}

const createHandler = (type, handler) => (el, binding, vnode) => {
  if (!el.useTouchHandlers) {
    el.useTouchHandlers = {}
  }

  if (!el.useTouchHandlers[binding.arg] && binding.arg === type) {
    el.useTouchHandlers[binding.arg] = handler(el, binding, vnode)
  }
}

const removeHandlers = (el, { arg }) => {
  if (el.useTouchHandlers && el.useTouchHandlers[arg]) {
    el.useTouchHandlers[arg]()
    delete el.useTouchHandlers[arg]
  }
}

const addSwipeHandler = createHandler('swipe', (el, { arg }, vnode) => {
  let useListener = touchSupported ? useSwipe : usePointerSwipe
  let swipeState = null

  el.useTouchOptions.onSwipeEnd = (evt) => {
    const disabled = el.useTouchOptions.disableClick && (
      useListener === usePointerSwipe
    )

    if (!disabled && swipeState) {
      const map = { up: 'top', down: 'bottom' }
      const dir = swipeState.direction.value.toLowerCase()
      const mod = map[dir] || dir

      eachCallback(el, arg, ({ type, callback }) => {
        if (type == null || type == mod) {
          callback.call(vnode.context, mod, evt)
        }
      })
    }
  }

  swipeState = useListener(el, el.useTouchOptions)
  return swipeState.stop
})

const onMounted = (el, binding, vnode) => {
  el.classList.add('touch-target')
  updateOptions(el, binding)

  addCallbacks(el, binding)
  addSwipeHandler(el, binding, vnode)
}

const onUnmounted = (el, binding) => {
  delete el.useTouchOptions

  if (el.classList) {
    el.classList.remove('touch-target')
  }

  removeCallbacks(el, binding)
  removeHandlers(el, binding)
}

const onUpdated = (el, binding) => {
  if (binding.arg == 'options') {
    updateOptions(el, binding)
  }
}

const Plugin = {
  install(Vue) {
    Vue.directive('touch', {
      bind: onMounted,
      update: onUpdated,
      unbind: onUnmounted
    })
  }
}

export default Plugin
